import { clsx } from 'clsx';
import type { GenerationProgress, SubTask, TaskStep, TaskStepStatus } from '@/types';

interface TaskProgressPanelProps {
  progress: GenerationProgress | null;
  className?: string;
  compact?: boolean;
}

/** Icon for step status */
function StepIcon({ status }: { status: TaskStepStatus }) {
  switch (status) {
    case 'completed':
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    case 'in_progress':
      return (
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      );
    case 'failed':
      return (
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    default:
      return <div className="w-4 h-4 rounded-full border-2 border-border" />;
  }
}

/** Step type label mapping */
const stepTypeLabels: Record<string, string> = {
  agent_research: 'Agent Research',
  aggregation: 'Aggregating',
  fact_checking: 'Fact Checking',
  debate: 'Resolving Debate',
};

/** Single step item */
function StepItem({ step, compact }: { step: TaskStep; compact?: boolean }) {
  return (
    <div className={clsx('flex items-center gap-2', compact ? 'text-xs' : 'text-sm')}>
      <StepIcon status={step.status} />
      <span className={clsx(
        step.status === 'completed' && 'text-text-secondary',
        step.status === 'in_progress' && 'text-primary font-medium',
        step.status === 'failed' && 'text-red-500',
        step.status === 'pending' && 'text-text-tertiary'
      )}>
        {stepTypeLabels[step.step_type] || step.name}
      </span>
    </div>
  );
}

/** Single task item with expandable steps */
function TaskItem({ task, isActive, compact }: { task: SubTask; isActive: boolean; compact?: boolean }) {
  const completedSteps = task.steps.filter(s => s.status === 'completed').length;
  const totalSteps = task.steps.length;
  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <div className={clsx(
      'border border-border rounded-lg p-3 transition-all',
      isActive && 'border-primary bg-primary/5',
      task.status === 'completed' && 'bg-green-500/5 border-green-500/30'
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StepIcon status={task.status} />
          <span className={clsx(
            'font-medium',
            compact ? 'text-sm' : 'text-base',
            task.status === 'completed' && 'text-green-600'
          )}>
            {task.name}
          </span>
        </div>
        {task.confidence_score !== undefined && (
          <span className={clsx(
            'text-xs px-2 py-0.5 rounded-full',
            task.confidence_score >= 0.8 ? 'bg-green-100 text-green-700' :
            task.confidence_score >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          )}>
            {(task.confidence_score * 100).toFixed(0)}% confidence
          </span>
        )}
      </div>

      {/* Progress bar */}
      {task.status !== 'pending' && (
        <div className="w-full h-1.5 bg-border rounded-full mb-2 overflow-hidden">
          <div
            className={clsx(
              'h-full transition-all duration-300',
              task.status === 'completed' ? 'bg-green-500' : 'bg-primary'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Steps (show when active or in compact mode) */}
      {(isActive || task.status === 'in_progress') && (
        <div className="space-y-1.5 mt-2 pl-2 border-l-2 border-border ml-2">
          {task.steps.map((step) => (
            <StepItem key={step.step_id} step={step} compact={compact} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Main progress panel */
export function TaskProgressPanel({ progress, className, compact = false }: TaskProgressPanelProps) {
  if (!progress) {
    return null;
  }

  const completedTasks = progress.sub_tasks.filter(t => t.status === 'completed').length;
  const totalTasks = progress.total_tasks;
  const overallProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={clsx('font-semibold text-text-primary', compact ? 'text-sm' : 'text-base')}>
            {progress.status === 'generating' ? 'Generating Newsletter...' :
             progress.status === 'completed' ? 'Generation Complete' :
             progress.status === 'failed' ? 'Generation Failed' : 'Queued'}
          </h3>
          <p className="text-xs text-text-secondary mt-0.5">
            {completedTasks} of {totalTasks} tasks complete
          </p>
        </div>
        <span className={clsx(
          'text-xs font-medium px-2 py-1 rounded-full',
          progress.status === 'generating' && 'bg-primary/10 text-primary',
          progress.status === 'completed' && 'bg-green-100 text-green-700',
          progress.status === 'failed' && 'bg-red-100 text-red-700',
          progress.status === 'queued' && 'bg-yellow-100 text-yellow-700'
        )}>
          {Math.round(overallProgress)}%
        </span>
      </div>

      {/* Overall progress bar */}
      <div className="w-full h-2 bg-border rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full transition-all duration-500',
            progress.status === 'completed' ? 'bg-green-500' :
            progress.status === 'failed' ? 'bg-red-500' : 'bg-primary'
          )}
          style={{ width: `${overallProgress}%` }}
        />
      </div>

      {/* Task list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {progress.sub_tasks.map((task, index) => (
          <TaskItem
            key={task.task_id}
            task={task}
            isActive={index === progress.current_task_index}
            compact={compact}
          />
        ))}
      </div>

      {/* Error message */}
      {progress.error_message && (
        <div className="text-sm text-red-500 bg-red-50 p-3 rounded-lg">
          {progress.error_message}
        </div>
      )}
    </div>
  );
}

